import { findNode } from '../graph/graphModel.js';
import { getHassInputPort, getHassNodeSchema, getHassOutputPort } from '../Hasp/properties/v2/hass/index.js';
import { resolveNodePorts } from '../Hasp/properties/v2/graphPorts.js';
import { HASS_NODE_SIZE, HASS_ROW, hassColumns } from './hassLayout.js';

const overlaps = (node, position) =>
    Math.abs(node.x - position.x) < HASS_NODE_SIZE.width && Math.abs(node.y - position.y) < HASS_ROW;

/** First position at or below `position` that does not overlap an existing node. */
export function nextFreeHassPosition(graph, position) {
    const candidate = { x: Math.round(position.x), y: Math.round(position.y) };
    while (graph.nodes.some(node => overlaps(node, candidate))) candidate.y += HASS_ROW;
    return candidate;
}

/**
 * Where a Home Assistant node added from the palette goes, and what it
 * connects to (see graphStoreOptions.addPaletteNode).
 *
 * Anchored on the selected UI object (`anchorObject: { uuid, rect }`, rect
 * in page coordinates) or the selected node (`anchorNodeId`):
 *   - an entity becomes the object's Primary Entity, feeds the selected
 *     binding (its entity), or is the target of the selected action;
 *   - a binding drives the object, or reads the selected entity;
 *   - a trigger listens to the object, or to the selected entity's state;
 *   - a condition branches from the selected trigger;
 *   - an action runs from the selected trigger or condition, or acts on the
 *     selected entity (its target).
 * An explicit `position` (a canvas drop) is used as is; otherwise the node
 * goes to the next free slot of its column (see hassLayout.js).
 *
 * @returns {{ position, connectFrom, connectTo, inputPort?, outputPort? }}
 */
export function planHassPaletteNode(graph, entry, { position, anchorObject, anchorNodeId, plateWidth = 0 } = {}) {
    const schema = getHassNodeSchema(entry.nodeType);
    const anchor = anchorNodeId ? findNode(graph, anchorNodeId) : null;
    const columns = hassColumns(plateWidth);
    const objectY = anchorObject?.rect?.y ?? 0;
    let connectFrom = null;
    let connectTo = null;
    let inputPort;
    let outputPort;
    let preferred = { x: columns.action, y: anchor?.y ?? objectY };

    switch (schema?.kind) {
    case 'entity':
        if (anchorObject) {
            connectTo = { object: anchorObject.uuid, port: 'in' };
        } else if (['binding', 'condition'].includes(anchor?.kind) && getHassInputPort(anchor.type, 'entity')) {
            connectTo = { node: anchor.id, port: 'entity' };
        } else if (anchor?.kind === 'action' && getHassOutputPort(anchor.type, 'target')) {
            connectFrom = { node: anchor.id, port: 'target' };
            inputPort = 'target';
        }
        preferred = connectFrom
            ? { x: columns.entityOut, y: anchor.y }
            : { x: columns.entityIn, y: anchor?.y ?? objectY };
        break;
    case 'binding':
        if (anchorObject) connectTo = { object: anchorObject.uuid, port: 'in' };
        else if (anchor?.kind === 'entity' && getHassInputPort(entry.nodeType, 'entity')) connectFrom = { node: anchor.id, port: 'state' };
        else if (anchor?.kind === 'switch') {
            // Into the switch's first case.
            connectFrom = { node: anchor.id, port: resolveNodePorts(getHassNodeSchema, anchor).outputs[0].id };
            inputPort = 'case';
        }
        preferred = { x: columns.binding, y: anchor?.y ?? objectY };
        break;
    case 'switch':
        if (anchor?.kind === 'entity') connectFrom = { node: anchor.id, port: 'state' };
        preferred = { x: columns.entityIn, y: anchor?.y ?? objectY };
        break;
    case 'trigger':
        if (anchorObject) connectFrom = { object: anchorObject.uuid, port: 'events' };
        else if (anchor?.kind === 'entity') connectFrom = { node: anchor.id, port: 'state' };
        preferred = { x: columns.trigger, y: anchor?.y ?? objectY };
        break;
    case 'condition':
        if (anchor?.kind === 'trigger') connectFrom = { node: anchor.id, port: 'run' };
        preferred = { x: columns.condition, y: anchor?.y ?? objectY };
        break;
    case 'action':
        if (anchor?.kind === 'trigger') connectFrom = { node: anchor.id, port: 'run' };
        else if (anchor?.kind === 'condition') connectFrom = { node: anchor.id, port: 'then' };
        else if (anchor?.kind === 'entity' && getHassOutputPort(entry.nodeType, 'target')) {
            connectTo = { node: anchor.id, port: 'target' };
            outputPort = 'target';
        }
        inputPort = 'run';
        break;
    default:
    }
    if (position) return { position: { x: Math.round(position.x), y: Math.round(position.y) }, connectFrom, connectTo, inputPort, outputPort };
    return { position: nextFreeHassPosition(graph, preferred), connectFrom, connectTo, inputPort, outputPort };
}
